import { keepPreviousData } from '@tanstack/react-query';
import { noddiAsync, URLKeys } from 'noddi-async';
import { ApiErrorMessageInternal, NoddiAsyncSelect, usePagination, type SelectOption } from 'noddi-ui-web';
import { useEffect, useMemo, useRef } from 'react';
import type { NotificationGroupOverviewTranslations } from './notificationTranslations';

type Props = {
  userGroupId: number;
  excludeUserGroupMemberId: number;
  /** Preselect the backend's default once, when that member is on the first page. */
  defaultReplacementId: number;
  selected?: SelectOption<number>;
  onSelect: (option: SelectOption<number>) => void;
  translations: NotificationGroupOverviewTranslations;
};

const memberLabel = (member: { firstName: string; lastName: string }) =>
  `${member.firstName} ${member.lastName}`.trim();

/** Other members of the organization, for the admin who is removing a driver. */
export const ReplacementMemberSelect = ({
  userGroupId,
  excludeUserGroupMemberId,
  defaultReplacementId,
  selected,
  onSelect,
  translations: t
}: Props) => {
  const { pageIndex, pageSize, search, setSearch } = usePagination();
  const didPreset = useRef(false);
  const { data, isFetching, error } = noddiAsync.useGet({
    type: URLKeys.getPaginatedNotificationOverviewMembers,
    input: { userGroupId, pageIndex, pageSize, search },
    queryConfig: { placeholderData: keepPreviousData }
  });

  const options = useMemo(
    () =>
      (data?.results ?? [])
        .filter((member) => member.userGroupMemberId !== excludeUserGroupMemberId)
        .map((member) => ({ value: member.userGroupMemberId, label: memberLabel(member) })),
    [data?.results, excludeUserGroupMemberId]
  );

  useEffect(() => {
    // One attempt against the unfiltered first page. A later search must not
    // overwrite a choice, and must not auto-pick the default if it scrolls in.
    if (didPreset.current || search !== '' || !data) {
      return;
    }
    didPreset.current = true;
    const match = options.find((option) => option.value === defaultReplacementId);
    if (match) {
      onSelect(match);
    }
  }, [data, defaultReplacementId, onSelect, options, search]);

  return (
    <div className='flex flex-col gap-2'>
      {error && <ApiErrorMessageInternal error={error} />}
      <NoddiAsyncSelect
        label={t.moveCarsTo}
        placeholder={t.chooseMember}
        searchPlaceholder={t.searchMembersPlaceholder}
        emptyMessage={t.noMembersFound}
        isLoading={isFetching}
        options={options}
        selectedOption={selected}
        clearable={false}
        onSelectionChange={(option) => {
          if (option) {
            onSelect(option);
          }
        }}
        onSearch={setSearch}
      />
    </div>
  );
};
